<template>
    <div :dir="$root.$i18n.locale == 'ar' ? 'rtl' : 'ltr'">
        <div class="tw-grid tw-grid-cols-12 tw-min-h-[100dvh] tw-relative">
            <div class="tw-relative tw-pb-24 tw-col-span-12 md:tw-col-span-7 lg:tw-col-span-5 2xl:tw-col-span-4 tw-h-full tw-flex tw-items-center tw-justify-center tw-gap-2 tw-flex-col tw-p-5">
                <div class="tw-w-full lg:tw-max-w-[350px]">
                    <AppLogo />
                </div>

                <div class="tw-w-full lg:tw-max-w-[350px] tw-mx-auto">
                    <h1 class="tw-mt-5 lg:tw-text-3xl tw-text-xl tw-w-full tw-text-start tw-font-bold tw-text-gray-800">
                        {{ $t('pages.reset-password.title') }} <span class="tw-text-primary-500">!</span>
                    </h1>
                    <p class="tw-mb-7 tw-mt-2 tw-text-sm tw-font-semibold">
                        {{ $t('pages.reset-password.subtitle') }}
                    </p>

                    <!-- Loading state while validating token -->
                    <div v-if="isValidating" class="tw-flex tw-items-center tw-gap-3 tw-mt-4">
                        <span class="tw-inline-block tw-w-4 tw-h-4 tw-border-2 tw-border-primary-500 tw-border-t-transparent tw-rounded-full tw-animate-spin"></span>
                        <span class="tw-text-sm tw-text-gray-600">
                            {{ $t('pages.reset-password.validating') }}
                        </span>
                    </div>

                    <!-- Invalid / expired token state -->
                    <div v-else-if="validationError" class="tw-mt-6 tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-lg tw-p-4">
                        <p class="tw-text-sm tw-font-semibold tw-text-red-700">
                            {{ $t(validationError) }}
                        </p>
                        <div class="tw-mt-4 tw-flex tw-justify-start">
                            <router-link :to="{ name: 'auth.login' }">
                                <a-button intent="secondary">
                                    {{ $t('pages.reset-password.back-to-login') }}
                                </a-button>
                            </router-link>
                        </div>
                    </div>

                    <!-- Valid token: show reset password form -->
                    <form v-else @submit="submit" class="tw-mt-4">
                        <FormField
                            v-model="form.password"
                            input-dir="ltr"
                            type="password"
                            placeholder="********"
                            left-icon="solar:lock-password-bold-duotone"
                            :label="$t('auth.new-password')"
                            :error="errors.password && $t('errors.' + errors.password, { field: $t('fields.new-password') })"
                        />

                        <div class="tw-mt-1"></div>

                        <FormField
                            v-model="form.password_confirmation"
                            input-dir="ltr"
                            type="password"
                            placeholder="********"
                            left-icon="solar:lock-password-bold-duotone"
                            :label="$t('auth.confirm-password')"
                            :error="errors.password_confirmation && $t('errors.' + errors.password_confirmation, { field: $t('fields.confirm-password') })"
                        />

                        <p v-if="errors.general" class="tw-text-xs tw-text-red-600 tw-mt-2">
                            {{ $t('errors.' + errors.general) }}
                        </p>

                        <div class="tw-mb-7"></div>

                        <a-button
                            intent="primary"
                            :loading="isSubmitting"
                            :disabled="isSubmitting"
                            full
                        >
                            {{ $t('pages.reset-password.submit') }}
                        </a-button>
                    </form>
                </div>

                <div class="tw-flex tw-items-center tw-justify-between tw-absolute tw-bottom-0 tw-p-5 tw-text-center tw-w-full">
                    <p class="tw-text-sm tw-text-gray-600">
                        {{ $t('pages.copyright', { year: (new Date()).getFullYear(), appName: APP_NAME }) }}
                    </p>
                    <AppLanguageSwitcher />
                </div>
            </div>

            <div class="tw-col-span-12 md:tw-col-span-5 lg:tw-col-span-7 2xl:tw-col-span-8 tw-relative tw-h-full tw-hidden md:tw-block">
                <img :src="$frontend('assets/images/banner-3.png')" class="tw-relative tw-z-20 tw-h-full tw-max-h-screen tw-w-full tw-object-contain tw-bg-left">
                <div class="tw-absolute tw-top-0 tw-left-0 tw-w-full tw-h-full tw-z-10 tw-bg-[#22154a] tw-flex tw-items-center tw-justify-center tw-p-8 lg:tw-p-12">
                    <div class="tw-text-center tw-text-white tw-max-w-lg">
                        <h2 class="tw-text-4xl lg:tw-text-5xl tw-font-bold tw-mb-4 tw-leading-tight tw-drop-shadow-lg tw-whitespace-nowrap">
                            {{ $t('pages.login.overlay-heading') }}
                        </h2>
                        <p class="tw-text-lg lg:tw-text-xl tw-font-medium tw-opacity-95 tw-leading-relaxed tw-drop-shadow-md">
                            {{ $t('pages.login.overlay-description') }}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { reactive, ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import AppLogo from '@/components/AppLogo';
import AppLanguageSwitcher from '@/components/AppLanguageSwitcher';
import FormField from '@/components/form/FormField';
import { APP_NAME } from '@/config/app';
import { validateResetToken, resetPassword } from '@/api/auth';

const route = useRoute();
const router = useRouter();

const token = ref('');
const email = ref('');

const isValidating = ref(true);
const validationError = ref('');
const isSubmitting = ref(false);

const form = reactive({
    password: '',
    password_confirmation: '',
});

const errors = reactive({
    password: '',
    password_confirmation: '',
    general: '',
});

onMounted(async () => {
    token.value = route.query.token || '';
    email.value = route.query.email || '';

    if (!token.value || !email.value) {
        validationError.value = 'pages.reset-password.invalid-link';
        isValidating.value = false;
        return;
    }

    try {
        await validateResetToken({
            token: token.value,
            email: email.value,
        });
        validationError.value = '';
    } catch (e) {
        // Map backend error to a generic invalid/expired key for now
        validationError.value = 'pages.reset-password.invalid-or-expired';
    } finally {
        isValidating.value = false;
    }
});

const submit = async ($e) => {
    $e.preventDefault();

    errors.password = '';
    errors.password_confirmation = '';
    errors.general = '';

    if (!form.password) {
        errors.password = 'required';
    }

    if (!form.password_confirmation) {
        errors.password_confirmation = 'required';
    }

    if (form.password && form.password_confirmation && form.password !== form.password_confirmation) {
        errors.password_confirmation = 'password_mismatch';
    }

    if (errors.password || errors.password_confirmation) {
        return;
    }

    isSubmitting.value = true;

    try {
        await resetPassword({
            token: token.value,
            email: email.value,
            password: form.password,
            password_confirmation: form.password_confirmation,
        });

        router.push({ name: 'auth.login' });
    } catch (e) {
        // Handle possible backend validation errors
        if (e?.response?.data?.errors) {
            const backendErrors = e.response.data.errors;

            if (backendErrors.password) {
                errors.password = backendErrors.password[0] || 'invalid';
            }

            if (backendErrors.password_confirmation) {
                errors.password_confirmation = backendErrors.password_confirmation[0] || 'invalid';
            }

            if (backendErrors.token) {
                validationError.value = 'pages.reset-password.invalid-or-expired';
            }
        } else {
            errors.general = 'something_went_wrong';
        }
    } finally {
        isSubmitting.value = false;
    }
};
</script>

<style>
</style>



